import { useCallback } from "react";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import useSnackbar from "../../../hooks/useSnackbar";
import { downloadFileFromBlob } from "../../../utils/files";
import {
  REJECTIONS_STATUS_COLUMN_KEY,
  REJECTIONS_STATUS_COLUMN_VALUES
} from "../constants";
import { BulkResource } from "../types";
import { getFlatfileMainSheetId } from "../utils/getFlatfileMainSheetId";
import { getUploadedFilename } from "../utils/getUploadedFilename";
import { useFlatfileApiClient } from "./useFlatfileApiClient";

function getSkippedRowsFilename(
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
): string {
  const uploadedFilename = getUploadedFilename(bulkBatch, bulkResource);
  // the uploaded file can be a spreadsheet of any format, the skipped rows are always a CSV
  const uploadedFilenameWithoutExtension = uploadedFilename.replace(
    /\.[^.]+$/,
    ""
  );

  return `Skipped_rows_${uploadedFilenameWithoutExtension || bulkResource}.csv`;
}

/**
 * Downloads the spreadsheet rows Flatfile could not upload into the batch, as a CSV.
 *
 * The rows stay in the Flatfile sheet, marked as rejected by the upload listener, so they are
 * fetched straight from Flatfile with the same filter the listener uses to show them in the
 * portal.
 */
export function useDownloadSkippedRows(
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
): () => Promise<void> {
  const { warning } = useSnackbar();
  const flatfileApiClient = useFlatfileApiClient(bulkBatch);

  return useCallback(async () => {
    try {
      if (!flatfileApiClient) {
        throw new Error("No Flatfile access token on the batch");
      }

      const sheetId = await getFlatfileMainSheetId(
        flatfileApiClient,
        bulkBatch,
        bulkResource
      );
      const csvStream = await flatfileApiClient.sheets.getRecordsAsCsv(
        sheetId,
        {
          searchField: REJECTIONS_STATUS_COLUMN_KEY,
          searchValue: REJECTIONS_STATUS_COLUMN_VALUES.Rejected
        }
      );
      // in the browser the Flatfile client resolves the download as a ReadableStream
      const csvBlob = await new Response(
        csvStream as unknown as ReadableStream
      ).blob();

      downloadFileFromBlob(
        csvBlob,
        getSkippedRowsFilename(bulkBatch, bulkResource)
      );
    } catch (error) {
      console.error("Failed to download skipped rows", error);
      warning("Failed to download the skipped rows. Please try again.");
    }
  }, [flatfileApiClient, bulkBatch, bulkResource, warning]);
}
